import 'swiper/css'
import 'swiper/css/navigation'

import { Box, BoxProps, Flex, Stack, StackProps } from '@chakra-ui/react'
import { LandingContainer } from 'components/common/LandingContainer'
import { PressSection } from 'components/sections/PressSection'
import { SafeEnvironmentSection } from 'components/sections/SafeEnvironmentSection'
import StepToTreatmentSlide from 'components/sections/StepsToTreatment/StepToTreatmentSlide'
import { Body1, Body2, Body3, H1, H2 } from 'components/typography'
import { FC, ReactNode } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { stats, stepsToTreatmentConcise, StepsToTreatmentData } from './stepsToTreatmentData'

interface StepsToTreatmentProps {
  title?: string
  showStats?: boolean
  steps?: StepsToTreatmentData[]
  stepsContainerProps?: BoxProps
  /** GEO home (PRD 6 and 7.3): where the numbers come from, and a block after the numbers. */
  statsSource?: string
  afterStats?: ReactNode
}

const StepsToTreatment: FC<StepsToTreatmentProps> = ({
  title = 'Deixe a sala de espera no passado e faça tudo online',
  showStats = false,
  steps = stepsToTreatmentConcise,
  stepsContainerProps,
  statsSource,
  afterStats
}) => {
  return (
    <Box bg="phthalo">
      <LandingContainer
        bg="phthalo"
        align="center"
        py={{ base: 12, lg: 32 }}
        gap={{ base: '40px', lg: '56px' }}
        overflow="hidden"
      >
        <Flex w="full" maxW={{ base: '400px', md: '640px', lg: '1120px' }}>
          <H2 flex="1" color="magicmint">
            {title}
          </H2>
          <Box
            flex={{
              base: 0,
              lg: 0.75
            }}
          />
        </Flex>

        <Box
          display={{
            base: 'none',
            lg: 'flex'
          }}
          rowGap="24px"
          {...stepsContainerProps}
        >
          {steps.map((step, index) => {
            return (
              <Flex key={index} flex={1}>
                <StepToTreatmentSlide {...step} stepIndex={index} />
              </Flex>
            )
          })}
        </Box>

        <Box
          display={{
            base: 'block',
            lg: 'none'
          }}
          maxW={{ base: '400px', md: '640px', lg: '1120px' }}
          px={{ base: '24px', lg: '12px' }}
        >
          <Swiper
            grabCursor={true}
            slidesPerView={1.2}
            style={{
              overflow: 'visible'
            }}
            breakpoints={{
              992: {
                slidesPerView: 4,
                pagination: false
              }
            }}
          >
            {steps.map((step, index) => {
              return (
                <SwiperSlide
                  key={index}
                  style={{
                    height: 'auto'
                  }}
                >
                  <StepToTreatmentSlide {...step} stepIndex={index} />
                </SwiperSlide>
              )
            })}
          </Swiper>
        </Box>
        <SectionFooter />
        {showStats && <Stats source={statsSource} />}
        {afterStats}
      </LandingContainer>
    </Box>
  )
}

export default StepsToTreatment

export const SectionFooter: FC = () => {
  return (
    <Stack
      direction={{ base: 'column', lg: 'row' }}
      gap={6}
      w="full"
      maxW={{ base: '400px', md: '640px', lg: '1120px' }}
    >
      <PressSection w="100%" />
      <SafeEnvironmentSection w="100%" />
    </Stack>
  )
}

/** The numbers block; `items` other numbers in the same style, `source` small text under them (GEO PRD section 6). */
export const Stats: FC<StackProps & { items?: { title: string; description: string }[]; source?: string }> = ({
  items = stats,
  source,
  ...props
}) => {
  return (
    <>
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        px={6}
        maxW="1280px"
        spacing="56px"
        pt={{ base: '80px', lg: '140px' }}
        pb={{ base: '80px', lg: '60px' }}
        {...props}
      >
        {items.map(({ title, description }) => {
          return (
            <Stack key={title} textAlign="center" spacing={{ base: 3, lg: 5 }} alignItems="center" flex="1">
              <H1 as="p" color="magicmint">
                {title}
              </H1>
              <Body2 color="magicmint" maxW="70%">
                {description}
              </Body2>
            </Stack>
          )
        })}
      </Stack>
      {source && (
        <Body3 color="magicmint" opacity={0.85} textAlign="center" px={6}>
          {source}
        </Body3>
      )}
    </>
  )
}
